/**
 * The journal as five buckets: what you are waiting for, what needs attention,
 * what is working, and the two kinds of over.
 *
 * ## Why these
 *
 * A plan list sorted by date answers "what did I do". These answer "what should
 * I look at now", which is a different question and the one a trading day is
 * actually spent on. Every open position lands in exactly one bucket and every
 * unfilled plan in another, so the counts sum and "how many positions do I
 * have" has an answer on screen.
 *
 * The first three are live and the last two are history, which is why they read
 * in that order: what needs an action today comes before what is only worth
 * studying.
 *
 * **`cancelled` is deliberately in no group.** A plan you called off is neither
 * completed nor missed — nothing happened, and the market never got a say. It
 * would quietly inflate the miss rate, which is the one statistic `missed`
 * exists to measure honestly.
 *
 * ## Time danger promotes a winner
 *
 * The subtle one. `danger().time` fires only when R is already **positive**:
 * it means "up, past the time limit, still carrying the whole original risk" —
 * a position going nowhere while its stop has not moved. So a plan at +0.2R
 * held past its limit shows in **Danger Zone**, not In Profit.
 *
 * That is the point of a time limit rather than a bug in the sorting: capital
 * sat in a trade that is not working is the loss nobody notices, because
 * nothing on the screen is red.
 *
 * ## Derived here, stored nowhere
 *
 * Membership is a function of the plan, its fills and the **live price**, so it
 * changes intraday and cannot be a stored group. `sym/groups` materialises
 * membership into a table on a schedule; that is right for SP500 and wrong for
 * this, which is why these are not a fourth `GroupType`. Same reasoning as
 * `plan-annotations.ts`: the journal carries facts and the view is computed.
 */

import type { Execution, Plan, Trade } from "@qufolio/model";

import { danger, netPosition, rMultiple } from "./plan-math";

/** Which bucket a plan is in, or `null` for one that belongs in none. */
export type TradeGroup = "plan" | "danger" | "profit" | "completed" | "missed";

export interface TradeGroupSpec {
  id: TradeGroup;
  label: string;
  /** What the tab means, for a tooltip and an empty state that teaches. */
  hint: string;
}

/**
 * The five, in the order they are read.
 *
 * Danger before profit on purpose: the tab someone opens the app to check is
 * the one that might need an action today, and putting the good news first
 * makes it the thing they see instead.
 */
export const TRADE_GROUPS: TradeGroupSpec[] = [
  {
    id: "plan",
    label: "Plan",
    hint: "Armed and waiting — no fill yet",
  },
  {
    id: "danger",
    label: "Danger Zone",
    hint: "Open and losing, near the stop, or past its time limit",
  },
  {
    id: "profit",
    label: "In Profit",
    hint: "Open and working",
  },
  {
    id: "completed",
    label: "Completed",
    hint: "Closed — the position is out",
  },
  {
    id: "missed",
    label: "Missed",
    hint: "The entry never filled and the move happened without you",
  },
];

/**
 * Every execution belonging to a plan, via its trade.
 *
 * A plan does not name its fills — a `Trade` names its plan and an `Execution`
 * names its trade — so the link is two hops and worth doing once rather than
 * inside a filter that runs per group.
 */
export function executionsByPlan(
  trades: readonly Trade[],
  executions: readonly Execution[],
): Map<string, Execution[]> {
  const tradeToPlan = new Map<string, string>();
  for (const trade of trades) {
    if (trade.planId) tradeToPlan.set(trade.id, trade.planId);
  }

  const out = new Map<string, Execution[]>();
  for (const execution of executions) {
    const planId = tradeToPlan.get(execution.tradeId);
    if (!planId) continue;
    const list = out.get(planId);
    if (list) list.push(execution);
    else out.set(planId, [execution]);
  }
  // Chronological, because `danger` measures the hold from the *first* fill and
  // a journal loaded in any other order would age the position wrongly.
  for (const list of out.values()) {
    list.sort((a, b) => Date.parse(a.executedAt) - Date.parse(b.executedAt));
  }
  return out;
}

/**
 * The bucket one plan belongs in, or `null` if it belongs in none.
 *
 * `price` is the symbol's last trade. Without one — a symbol the chart has not
 * loaded — an open position cannot be judged either way, so it reports as
 * danger rather than profit: an unknown is a thing to look at, and the opposite
 * default would quietly hide a position that is losing.
 */
export function groupFor(
  plan: Plan,
  executions: readonly Execution[],
  price: number | undefined,
  now: Date = new Date(),
): TradeGroup | null {
  // A plan you called off is neither completed nor missed — see the module
  // docstring. It is the one status with no home, on purpose.
  if (plan.status === "cancelled") return null;
  if (plan.status === "missed") return "missed";
  if (plan.status === "closed") return "completed";

  const { quantity } = netPosition(executions, plan.direction);
  if (Math.abs(quantity) === 0) {
    // Nothing held. Two very different reasons, and the fills tell them apart:
    // never entered (still waiting) against entered and scaled all the way out
    // (done, with the status not yet caught up). Reading both as "plan" would
    // put a finished trade back among the ones you are waiting for.
    return executions.length > 0 ? "completed" : "plan";
  }

  if (price === undefined || !Number.isFinite(price)) return "danger";

  const flags = danger(plan, executions, price, now);
  if (flags.price || flags.time) return "danger";
  return rMultiple(plan, price) > 0 ? "profit" : "danger";
}

export type GroupedPlans = Record<TradeGroup, Plan[]>;

/**
 * Every plan bucketed at once.
 *
 * `prices` is keyed by symbol, uppercased by the caller or not — a symbol
 * reaches this from a chart, a URL and a form, and only one of those is
 * guaranteed to have uppercased it.
 */
export function groupPlans(
  plans: readonly Plan[],
  trades: readonly Trade[],
  executions: readonly Execution[],
  prices: ReadonlyMap<string, number>,
  now: Date = new Date(),
): GroupedPlans {
  const fills = executionsByPlan(trades, executions);
  // Built from the list so a group added above cannot be forgotten here.
  const out = Object.fromEntries(
    TRADE_GROUPS.map((g) => [g.id, [] as Plan[]]),
  ) as GroupedPlans;

  for (const p of plans) {
    const group = groupFor(p, fills.get(p.id) ?? [], prices.get(p.symbol.toUpperCase()), now);
    if (group) out[group].push(p);
  }
  return out;
}
